'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useOutcomeTokenSnapshot } from '@/hooks/gifts/use-outcome-token-snapshot';
import { useFormattedCurrency } from '@/lib/currency';
import { formatShares } from '@/lib/format/format-shares';
import { estimateOutcomeTokenSpotValueFromStringsD6 } from '@/lib/gifts/outcome-token-spot-value';
export interface OutcomeTokenDisplayProps {
  /** Mint pubkey (base58). Drives the live-price lookup. */
  mintPubkey: string;
  /** Raw amount string in base units (D6). */
  amountBaseUnits: string;
  /**
   * Backend-supplied label fallback (e.g. from the contributors endpoint). Used
   * when the live `useGetOutcome` lookup is still loading or unavailable.
   */
  fallbackOutcomeLabel?: string | null;
  /**
   * Backend-supplied market title fallback (e.g. from the contributors
   * endpoint). Used while the live `useGetMarket` lookup is still loading.
   */
  fallbackMarketTitle?: string | null;
  /** Compact layout — single right-aligned column. Default: false (two lines). */
  compact?: boolean;
}

/**
 * A small pulsing dot indicating the price next to it is live. Honors
 * `motion-reduce:animate-none` so users with reduced-motion don't see it pulse.
 */
function LiveIndicator(t0) {
  const $ = _c(2);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span role="img" aria-label={label} className="inline-block size-2 rounded-full bg-feedback-success-accent motion-safe:animate-pulse" />;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Renders the canonical outcome-token gift display:
 *   Top line  — "{shares} shares · {outcomeLabel}"
 *   Sub line  — "{marketTitle}"
 *   Right     — indicative marginal-spot value with a live-price indicator
 *
 * Used by every gift surface that needs to present an outcome-token deposit
 * (gift-row, vault-claim-group rows, contributor-table rows, claim-success
 * modal rows, gift-permalink-card).
 */
export function OutcomeTokenDisplay(props) {
  const $ = _c(34);
  const t = useTranslations("gift.outcome_display");
  const {
    formatD6
  } = useFormattedCurrency();
  const snapshot = useOutcomeTokenSnapshot(props.mintPubkey);
  let marketTitle;
  let t0;
  if ($[0] !== props.amountBaseUnits || $[1] !== props.fallbackMarketTitle || $[2] !== props.fallbackOutcomeLabel || $[3] !== props.mintPubkey || $[4] !== snapshot.marketTitle || $[5] !== snapshot.outcomeLabel || $[6] !== t) {
    const outcomeLabel = snapshot.outcomeLabel ?? props.fallbackOutcomeLabel ?? props.mintPubkey.slice(0, 8);
    marketTitle = snapshot.marketTitle ?? props.fallbackMarketTitle ?? null;
    const sharesText = formatShares(props.amountBaseUnits);
    t0 = t("shares_with_outcome", {
      shares: sharesText,
      outcome: outcomeLabel
    });
    $[0] = props.amountBaseUnits;
    $[1] = props.fallbackMarketTitle;
    $[2] = props.fallbackOutcomeLabel;
    $[3] = props.mintPubkey;
    $[4] = snapshot.marketTitle;
    $[5] = snapshot.outcomeLabel;
    $[6] = t;
    $[7] = marketTitle;
    $[8] = t0;
  } else {
    marketTitle = $[7];
    t0 = $[8];
  }
  const sharesLabel = t0;
  let t1;
  bb0: {
    if (snapshot.currentPriceD9 === null) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[9] !== formatD6 || $[10] !== props.amountBaseUnits || $[11] !== snapshot.currentPriceD9) {
      const valueD6 = estimateOutcomeTokenSpotValueFromStringsD6(props.amountBaseUnits, snapshot.currentPriceD9);
      t2 = valueD6 === null ? null : formatD6(valueD6.toString());
      $[9] = formatD6;
      $[10] = props.amountBaseUnits;
      $[11] = snapshot.currentPriceD9;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = t2;
  }
  const valueUsdcDisplay = t1;
  if (props.compact === true) {
    let t2;
    if ($[13] !== sharesLabel) {
      t2 = <span className="max-w-full truncate text-body font-medium" title={sharesLabel}>{sharesLabel}</span>;
      $[13] = sharesLabel;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    let t3;
    if ($[15] !== t || $[16] !== valueUsdcDisplay) {
      t3 = valueUsdcDisplay !== null ? <span className="inline-flex items-center gap-1 text-caption text-foreground-secondary"><LiveIndicator label={t("live_price_indicator")} /><span data-testid="outcome-live-value">{valueUsdcDisplay}</span></span> : null;
      $[15] = t;
      $[16] = valueUsdcDisplay;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    let t4;
    if ($[18] !== t2 || $[19] !== t3) {
      t4 = <div className="flex min-w-0 max-w-full flex-col items-end gap-0.5">{t2}{t3}</div>;
      $[18] = t2;
      $[19] = t3;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    return t4;
  }
  let t2;
  if ($[21] !== sharesLabel) {
    t2 = <span className="truncate text-body font-medium">{sharesLabel}</span>;
    $[21] = sharesLabel;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== marketTitle) {
    t3 = marketTitle !== null && marketTitle.length > 0 ? <span className="truncate text-caption text-foreground-secondary">{marketTitle}</span> : null;
    $[23] = marketTitle;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  let t4;
  if ($[25] !== t2 || $[26] !== t3) {
    t4 = <div className="flex min-w-0 flex-col gap-0.5">{t2}{t3}</div>;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== t || $[29] !== valueUsdcDisplay) {
    t5 = valueUsdcDisplay !== null ? <div className="flex shrink-0 flex-col items-end gap-0.5"><span className="text-body-lg font-medium">{valueUsdcDisplay}</span><span className="inline-flex items-center gap-1 text-caption text-foreground-secondary"><LiveIndicator label={t("live_price_indicator")} />{t("live_price_label")}</span></div> : null;
    $[28] = t;
    $[29] = valueUsdcDisplay;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== t4 || $[32] !== t5) {
    t6 = <div className="flex w-full items-center justify-between gap-2">{t4}{t5}</div>;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  return t6;
}